import React, { useState } from "react";
import { DRAWING_COLORS, TOOL_LABELS, rrInfo, isPositionTool, EMOJI_SET } from "../../engine";
import { UI } from "../../design/icons/ChartIconRegistry";

// Floating toolbar for the selected drawing: style, lock, hide, duplicate, pin-to-timeframe, text, RR sizing, tool-specific settings, delete.
export default function ObjectToolbar({ drawing: d, tfId, onUpdate, onRemove, onDuplicate, onDeselect }) {
  const [editing, setEditing] = useState(false);
  if (!d) return null;
  const setStyle = (patch) => onUpdate(d.id, (o) => ({ style: { ...o.style, ...patch } }));
  const setMeta = (patch) => onUpdate(d.id, (o) => ({ meta: { ...o.meta, ...patch } }));
  const hasText = d.text != null;
  const rr = isPositionTool(d.type) ? rrInfo(d) : null;
  const extra = hasText || rr || d.type === "emoji" || d.type === "tickcontract" || d.type === "touchbarrier";

  return (
    <div data-testid="object-toolbar" className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 px-2 h-9 rounded-xl border border-border bg-popover/95 backdrop-blur shadow-lg text-xs max-w-[95%] overflow-x-auto scrollbar-thin">
      <span className="font-semibold text-primary pr-1 whitespace-nowrap" data-testid="object-toolbar-type">{TOOL_LABELS[d.type]}</span>
      <Sep />
      <div className="flex items-center gap-0.5">
        {DRAWING_COLORS.map((c) => (
          <button key={c} data-testid={`object-color-${c.slice(1)}`} onClick={() => setStyle({ color: c })} title={c}
            className={`h-4 w-4 rounded-full border ${d.style.color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{ background: c }} />
        ))}
      </div>
      <Sep />
      {[1, 2, 3].map((w) => (
        <button key={w} data-testid={`object-width-${w}`} onClick={() => setStyle({ width: d.type === "highlighter" ? w * 7 : w })} title={`Width ${w}`}
          className={`h-6 w-6 rounded flex items-center justify-center ${(d.type === "highlighter" ? d.style.width / 7 : d.style.width) === w ? "bg-primary/20 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
          <span className="w-3 bg-current rounded" style={{ height: w }} />
        </button>
      ))}
      <button data-testid="object-dash" onClick={() => setStyle({ dash: d.style.dash?.length ? [] : [6, 4] })} title="Dashed"
        className={`h-6 px-1.5 rounded font-mono ${d.style.dash?.length ? "bg-primary/20 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>- -</button>
      <Sep />
      {hasText && (
        editing ? (
          <input autoFocus data-testid="object-text-input" defaultValue={d.text || ""} onBlur={(e) => { onUpdate(d.id, { text: e.target.value }); setEditing(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(false); e.stopPropagation(); }}
            className="h-6 w-40 px-1.5 rounded border border-border bg-background text-xs" />
        ) : (
          <Btn testid="object-text-edit" title="Edit text" onClick={() => setEditing(true)} icon={UI.text} />
        )
      )}
      {rr && (
        <div className="flex items-center gap-1 font-mono-nums" data-testid="object-rr-settings">
          <label className="text-muted-foreground">Bal</label>
          <input data-testid="object-rr-balance" type="number" value={d.meta?.balance ?? 10000} onChange={(e) => setMeta({ balance: +e.target.value })} className="h-6 w-20 px-1 rounded border border-border bg-background" />
          <label className="text-muted-foreground">Risk %</label>
          <input data-testid="object-rr-risk" type="number" step="0.1" value={d.meta?.riskPct ?? 1} onChange={(e) => setMeta({ riskPct: +e.target.value })} className="h-6 w-14 px-1 rounded border border-border bg-background" />
          <span className="text-muted-foreground whitespace-nowrap" data-testid="object-rr-ratio">R:R 1:{rr.ratio.toFixed(2)}{rr.lot != null ? ` · ${rr.lot.toFixed(2)} lot` : ""}</span>
        </div>
      )}
      {d.type === "emoji" && (
        <div className="flex items-center gap-0.5" data-testid="object-emoji-picker">
          {EMOJI_SET.slice(0, 12).map((em) => (
            <button key={em} data-testid={`object-emoji-${em.codePointAt(0).toString(16)}`} onClick={() => setMeta({ emoji: em })} className={`h-6 w-6 rounded text-sm leading-none ${d.meta?.emoji === em ? "bg-primary/20" : "hover:bg-secondary"}`}>{em}</button>
          ))}
        </div>
      )}
      {d.type === "tickcontract" && (
        <div className="flex items-center gap-1 font-mono-nums" data-testid="object-tick-settings">
          {["rise", "fall"].map((dir) => (
            <button key={dir} data-testid={`object-tick-${dir}`} onClick={() => setMeta({ direction: dir })}
              className={`h-6 px-2 rounded font-semibold ${(d.meta?.direction ?? "rise") === dir ? (dir === "rise" ? "bg-buy/20 text-buy" : "bg-sell/20 text-sell") : "text-muted-foreground hover:bg-secondary"}`}>{dir === "rise" ? "Rise ▲" : "Fall ▼"}</button>
          ))}
          <label className="text-muted-foreground">Ticks</label>
          <input data-testid="object-tick-count" type="number" min="1" max="10" value={d.meta?.ticks ?? 5} onChange={(e) => setMeta({ ticks: Math.max(1, Math.min(10, +e.target.value || 1)) })} className="h-6 w-12 px-1 rounded border border-border bg-background" />
        </div>
      )}
      {d.type === "touchbarrier" && (
        <div className="flex items-center gap-0.5" data-testid="object-touch-kind">
          {["touch", "notouch"].map((k) => (
            <button key={k} data-testid={`object-touch-${k}`} onClick={() => setMeta({ kind: k })} className={`h-6 px-2 rounded ${(d.meta?.kind ?? "touch") === k ? "bg-primary/20 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>{k === "touch" ? "Touch" : "No Touch"}</button>
          ))}
        </div>
      )}
      {extra && <Sep />}
      <Btn testid="object-pin" title={d.timeframe ? `Pinned to ${d.timeframe} — click to show on all timeframes` : `Show only on ${tfId}`} onClick={() => onUpdate(d.id, { timeframe: d.timeframe ? null : tfId })} icon={d.timeframe ? UI.unpin : UI.pin} active={!!d.timeframe} />
      <Btn testid="object-lock" title={d.locked ? "Unlock" : "Lock"} onClick={() => onUpdate(d.id, { locked: !d.locked })} icon={d.locked ? UI.lock : UI.unlock} active={d.locked} />
      <Btn testid="object-hide" title="Hide" onClick={() => { onUpdate(d.id, { visible: false }); onDeselect(); }} icon={UI.hide} />
      <Btn testid="object-duplicate" title="Duplicate (Ctrl+D)" onClick={() => onDuplicate(d.id)} icon={UI.duplicate} />
      <Btn testid="object-delete" title="Delete (Del)" onClick={() => onRemove(d.id)} icon={UI.trash} danger />
      <Sep />
      <Btn testid="object-deselect" title="Deselect (Esc)" onClick={onDeselect} icon={UI.close} />
    </div>
  );
}

const Sep = () => <span className="h-5 w-px bg-border mx-0.5 shrink-0" />;

function Btn({ icon: Icon, onClick, title, testid, active, danger }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`h-6 w-6 shrink-0 rounded flex items-center justify-center ${active ? "bg-primary/20 text-primary" : danger ? "text-muted-foreground hover:bg-secondary hover:text-sell" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
      <Icon size={14} />
    </button>
  );
}
